import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFetch } from "@/hooks/useFetch";
import { usePermissions } from "@/lib/permissions";
import { onRealtimeEvent } from "@/lib/socket";
import { centroid, neighbourRect, rectToRing, ringToRect, transformRing } from "@/lib/parkingGeometry";
import { parkingService } from "@/services/api";
import { relativeTime } from "@/lib/format";
import { ParkingMap } from "@/components/parking/ParkingMap";
import { ParkingAreaDialog } from "@/components/parking/ParkingAreaDialog";
import { ConfirmDialog } from "@/components/dialogs/ConfirmDialog";
import { StatItem } from "@/components/common/Telemetry";
import { SkeletonMap } from "@/components/common/Skeletons";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  ParkingSquare, Plus, Trash2, Car, X, Pencil, Check, Loader2, Copy, Move,
} from "lucide-react";

// Live parking board for one facility: every slot of a lot coloured by
// occupancy, who is in each, and a manager's controls for building the lot
// area by area and correcting it by hand. Occupancy itself is computed
// server-side from vehicle positions (backend/app/services/parking_evaluator.py);
// this renders it and keeps it fresh from the `parking.slot` realtime event.
//
// Two ways to change geometry, chosen by the "Edit slots" switch:
//   off  the unit is a whole area: edit its scan, move it, rotate it, duplicate it
//   on   the unit is one slot (always a rectangle): move, resize, rotate, duplicate
// Nothing is sent until Save.

const REFRESH_DEBOUNCE_MS = 400;
const NO_PENDING = { updates: {}, deletes: [], creates: [] };
const NO_AREA_MOVE = { east: 0, north: 0, deg: 0 };
const isTmp = (id) => String(id).startsWith("tmp-");
const wrapDeg = (d) => ((((d + 180) % 360) + 360) % 360) - 180;

export function ParkingPanel({ facility }) {
  const navigate = useNavigate();
  const { isManager, isOperator } = usePermissions();

  const { data: layouts, loading: layoutsLoading, refetch: refetchLayouts } = useFetch(
    () => parkingService.layouts(facility.id),
    [facility.id]
  );
  const [selectedLayoutId, setSelectedLayoutId] = useState(null);
  const [selectedSlotId, setSelectedSlotId] = useState(null);
  const [dialog, setDialog] = useState(null); // null | "layout" | "area" | { edit: area }
  const [confirm, setConfirm] = useState(null); // null | "layout" | "release" | { area }
  const [menu, setMenu] = useState(null); // { x, y, slotId } — right-click menu on the map

  // Slot editing: changes are held here until "Save changes".
  const [editMode, setEditMode] = useState(false);
  const [pending, setPending] = useState(NO_PENDING);
  const [savingEdits, setSavingEdits] = useState(false);
  const tmpCounter = useRef(0);

  // Area move / rotate: the area being transformed and the shift so far.
  const [areaTool, setAreaTool] = useState(null); // { areaId } | null
  const [areaMove, setAreaMove] = useState(NO_AREA_MOVE);
  const [savingArea, setSavingArea] = useState(false);

  // Default to the first lot; fall back to it if the open one was deleted.
  useEffect(() => {
    if (!layouts || layouts.length === 0) {
      setSelectedLayoutId(null);
      return;
    }
    if (!layouts.some((l) => l.id === selectedLayoutId)) setSelectedLayoutId(layouts[0].id);
  }, [layouts, selectedLayoutId]);

  const { data: board, loading: boardLoading, refetch: refetchBoard } = useFetch(
    () => (selectedLayoutId ? parkingService.layout(selectedLayoutId) : Promise.resolve(null)),
    [selectedLayoutId]
  );

  // Live updates. A burst of arrivals becomes one refetch, and only events for
  // the open lot trigger it.
  const timer = useRef(null);
  const selectedRef = useRef(selectedLayoutId);
  selectedRef.current = selectedLayoutId;
  useEffect(() => {
    const off = onRealtimeEvent("parking.slot", (evt) => {
      if (evt?.layout_id !== selectedRef.current) return;
      clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        refetchBoard();
        refetchLayouts();
      }, REFRESH_DEBOUNCE_MS);
    });
    return () => {
      off();
      clearTimeout(timer.current);
    };
  }, [refetchBoard, refetchLayouts]);

  // A different lot: drop selection and any unsaved edits.
  useEffect(() => {
    setSelectedSlotId(null);
    setEditMode(false);
    setPending(NO_PENDING);
    setAreaTool(null);
    setAreaMove(NO_AREA_MOVE);
    setMenu(null);
  }, [selectedLayoutId]);

  // Dismiss the right-click menu on any click elsewhere or Escape.
  useEffect(() => {
    if (!menu) return undefined;
    const close = () => setMenu(null);
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("click", close);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("keydown", onKey);
    };
  }, [menu]);

  const refreshAll = useCallback(() => {
    refetchBoard();
    refetchLayouts();
  }, [refetchBoard, refetchLayouts]);

  const busy = editMode || !!areaTool; // a geometry edit is in progress
  const editCount = Object.keys(pending.updates).length + pending.deletes.length + pending.creates.length;

  // --- the area being moved / rotated
  const toolArea = useMemo(
    () => (areaTool ? (board?.areas || []).find((a) => a.id === areaTool.areaId) || null : null),
    [areaTool, board]
  );
  const toolPivot = useMemo(() => (toolArea ? centroid(toolArea.corners) : null), [toolArea]);
  const toolCenter = useMemo(
    () => (toolPivot ? transformRing([toolPivot], toolPivot, areaMove.deg, areaMove.east, areaMove.north)[0] : null),
    [toolPivot, areaMove]
  );

  // The board's slots with every unsaved change layered on, so the map shows
  // the result before it is saved.
  const mapSlots = useMemo(() => {
    if (!board?.slots) return [];
    let list = board.slots;
    if (toolArea) {
      list = list.map((s) => (s.areaId === toolArea.id
        ? { ...s, polygon: transformRing(s.polygon, toolPivot, areaMove.deg, areaMove.east, areaMove.north) }
        : s));
    }
    if (editMode) {
      list = list
        .filter((s) => !pending.deletes.includes(s.id))
        .map((s) => (pending.updates[s.id] ? { ...s, polygon: pending.updates[s.id] } : s))
        .concat(pending.creates.map((c) => ({
          id: c.id, areaId: c.areaId, number: "+", row: c.row, col: c.col,
          polygon: c.polygon, occupied: false, preview: true,
        })));
    }
    return list;
  }, [board, editMode, pending, toolArea, toolPivot, areaMove]);

  const outlines = useMemo(
    () => (board?.areas || []).map((a) => ({
      id: a.id,
      name: a.name,
      corners: toolArea && a.id === toolArea.id
        ? transformRing(a.corners, toolPivot, areaMove.deg, areaMove.east, areaMove.north)
        : a.corners,
    })),
    [board, toolArea, toolPivot, areaMove]
  );

  const selectedSlot = useMemo(
    () => (board?.slots || []).find((s) => s.id === selectedSlotId) || null,
    [board, selectedSlotId]
  );
  const selectedEditable = useMemo(
    () => mapSlots.find((s) => s.id === selectedSlotId) || null,
    [mapSlots, selectedSlotId]
  );
  const occupiedSlots = useMemo(
    () => (board?.slots || []).filter((s) => s.occupied).sort((a, b) => a.number - b.number),
    [board]
  );
  const areaName = useMemo(() => {
    const names = new Map((board?.areas || []).map((a) => [a.id, a.name]));
    return (id) => names.get(id);
  }, [board]);

  const center = facility.latitude != null && facility.longitude != null
    ? { lat: facility.latitude, lng: facility.longitude }
    : null;

  const total = board?.totalSlots ?? 0;
  const occupied = board?.occupiedSlots ?? 0;
  const pct = total ? Math.round((occupied / total) * 100) : 0;

  // ------------------------------------------------------------ lot actions
  const toggleActive = async (value) => {
    try {
      await parkingService.update(board.id, { isActive: value });
      refreshAll();
      toast.success(value ? "Parking tracking resumed" : "Parking tracking paused");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const removeLayout = async () => {
    try {
      await parkingService.remove(board.id);
      toast.success("Parking lot deleted");
      setSelectedLayoutId(null);
      refetchLayouts();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const releaseSlot = async () => {
    try {
      await parkingService.releaseSlot(selectedSlot.id);
      toast.success(`Slot #${selectedSlot.number} marked vacant`);
      refreshAll();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // ----------------------------------------------------------- area actions
  const removeArea = async (area) => {
    try {
      await parkingService.removeArea(area.id);
      toast.success(`${area.name} deleted`);
      setSelectedSlotId(null);
      refreshAll();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const startAreaTool = (areaId) => {
    setSelectedSlotId(null);
    setEditMode(false);
    setPending(NO_PENDING);
    setAreaMove(NO_AREA_MOVE);
    setAreaTool({ areaId });
  };
  const cancelAreaTool = () => {
    setAreaTool(null);
    setAreaMove(NO_AREA_MOVE);
  };
  const saveAreaTool = async () => {
    setSavingArea(true);
    try {
      const { overlaps } = await parkingService.transformArea(areaTool.areaId, {
        eastM: areaMove.east, northM: areaMove.north, rotateDeg: areaMove.deg,
      });
      toast.success(`${toolArea?.name ?? "Area"} moved`);
      if (overlaps.length) toast.warning(`It now overlaps ${overlaps.join(", ")}`);
      cancelAreaTool();
      refreshAll();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingArea(false);
    }
  };
  const onAreaMove = useCallback(({ east, north }) => {
    setAreaMove((m) => ({ ...m, east: m.east + east, north: m.north + north }));
  }, []);
  const onAreaRotate = useCallback((deltaDeg) => {
    setAreaMove((m) => ({ ...m, deg: wrapDeg(m.deg + deltaDeg) }));
  }, []);

  // The copy is saved straight away, just south of the original; moving it into
  // place afterwards is the normal move tool, opened on the copy.
  const duplicateArea = async (areaId) => {
    try {
      const { area, overlaps } = await parkingService.duplicateArea(areaId);
      toast.success(`${area.name} added — drag it into place, then Save`);
      if (overlaps.length) toast.warning(`The copy overlaps ${overlaps.join(", ")}`);
      refreshAll();
      startAreaTool(area.id);
    } catch (err) {
      toast.error(err.message);
    }
  };

  // ------------------------------------------------------------ slot edits
  const onSlotChange = useCallback((id, ring) => {
    setPending((p) => (isTmp(id)
      ? { ...p, creates: p.creates.map((c) => (c.id === id ? { ...c, polygon: ring } : c)) }
      : { ...p, updates: { ...p.updates, [id]: ring } }));
  }, []);

  const duplicateSelectedSlot = () => {
    const src = selectedEditable;
    if (!src) return;
    const id = `tmp-${++tmpCounter.current}`;
    setPending((p) => ({
      ...p,
      creates: [...p.creates, {
        id, areaId: src.areaId, row: src.row ?? 0, col: src.col ?? 0,
        polygon: rectToRing(neighbourRect(ringToRect(src.polygon))),
      }],
    }));
    setSelectedSlotId(id);
  };

  const deleteSelectedSlot = () => {
    const src = selectedEditable;
    if (!src) return;
    if (isTmp(src.id)) {
      setPending((p) => ({ ...p, creates: p.creates.filter((c) => c.id !== src.id) }));
    } else if (src.occupied) {
      toast.error(`Slot #${src.number} is occupied — release it first`);
      return;
    } else {
      setPending((p) => {
        const { [src.id]: _dropped, ...updates } = p.updates;
        return { ...p, updates, deletes: [...p.deletes, src.id] };
      });
    }
    setSelectedSlotId(null);
  };

  const leaveEditMode = () => {
    setEditMode(false);
    setPending(NO_PENDING);
    setSelectedSlotId(null);
  };

  const saveEdits = async () => {
    setSavingEdits(true);
    try {
      await parkingService.editSlots(board.id, {
        updates: Object.entries(pending.updates).map(([id, polygon]) => ({ id, polygon })),
        deletes: pending.deletes,
        creates: pending.creates.map((c) => ({ areaId: c.areaId, polygon: c.polygon, row: c.row, col: c.col })),
      });
      toast.success("Slot changes saved");
      leaveEditMode();
      refreshAll();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingEdits(false);
    }
  };

  // ------------------------------------------------------- right-click menu
  const onSlotContextMenu = useCallback((slotId, point) => {
    if (areaTool || slotId == null) return;
    setSelectedSlotId(editMode ? slotId : null);
    setMenu({ ...point, slotId });
  }, [areaTool, editMode]);

  const menuArea = menu && !editMode
    ? (board?.slots || []).find((s) => s.id === menu.slotId)?.areaId
    : null;
  const menuAreaObj = menuArea ? (board?.areas || []).find((a) => a.id === menuArea) : null;

  // ---------------------------------------------------------------- render
  const header = (
    <div className="mb-3 flex flex-wrap items-center gap-3">
      <h2 className="flex items-center gap-2 font-display text-lg font-semibold tracking-tight">
        <ParkingSquare className="h-5 w-5 text-muted-foreground" /> Parking
      </h2>
      {layouts && layouts.length > 1 && (
        <div className="flex flex-wrap gap-1.5">
          {layouts.map((l) => (
            <button
              key={l.id}
              onClick={() => setSelectedLayoutId(l.id)}
              disabled={busy}
              className={cn(
                "rounded-lg border px-2.5 py-1 text-xs transition",
                l.id === selectedLayoutId
                  ? "border-primary/60 bg-primary/10 text-foreground"
                  : "border-border/60 text-muted-foreground hover:text-foreground"
              )}
            >
              {l.name}
            </button>
          ))}
        </div>
      )}
      {isManager && (
        <div className="ml-auto flex gap-2">
          {board && (
            <Button size="sm" variant="secondary" onClick={() => setDialog("area")} disabled={busy} data-testid="add-parking-area">
              <Plus className="mr-1.5 h-4 w-4" /> Add area
            </Button>
          )}
          <Button size="sm" variant="secondary" onClick={() => setDialog("layout")} disabled={busy} data-testid="add-parking-layout">
            <Plus className="mr-1.5 h-4 w-4" /> Add lot
          </Button>
        </div>
      )}
    </div>
  );

  const dialogs = (
    <>
      <ParkingAreaDialog
        key="layout"
        open={dialog === "layout"}
        onOpenChange={(o) => !o && setDialog(null)}
        facility={facility}
        mode="layout"
        onSaved={(layout) => {
          refetchLayouts();
          setSelectedLayoutId(layout.id);
        }}
      />
      <ParkingAreaDialog
        key="area"
        open={dialog === "area"}
        onOpenChange={(o) => !o && setDialog(null)}
        facility={facility}
        mode="area"
        layout={board}
        onSaved={refreshAll}
      />
      <ParkingAreaDialog
        key="edit"
        open={!!dialog?.edit}
        onOpenChange={(o) => !o && setDialog(null)}
        facility={facility}
        mode="edit"
        layout={board}
        area={dialog?.edit}
        onSaved={refreshAll}
      />
    </>
  );

  if (layoutsLoading && !layouts) {
    return <section className="mt-8">{header}<SkeletonMap className="h-[360px]" /></section>;
  }

  if (!layouts || layouts.length === 0) {
    return (
      <section className="mt-8" data-testid="parking-panel">
        {header}
        <EmptyState
          icon={ParkingSquare}
          title="No parking lot yet"
          description="Scan the lot on the map one area at a time and the slots are generated for you. Vehicles are then tracked into and out of individual slots."
          action={isManager ? "Add parking lot" : undefined}
          onAction={() => setDialog("layout")}
          className="py-12"
        />
        {dialogs}
      </section>
    );
  }

  const menuItem = "flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-secondary/60";

  return (
    <section className="mt-8" data-testid="parking-panel">
      {header}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatItem label="Total slots" value={total} />
        <StatItem label="Occupied" value={occupied} />
        <StatItem label="Vacant" value={board?.vacantSlots ?? 0} />
        <StatItem label="Occupancy" value={`${pct}%`} />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_360px]">
        <div className="space-y-2">
          {editMode && (
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-primary/40 bg-primary/10 px-3 py-2 text-sm" data-testid="parking-edit-bar">
              <Pencil className="h-4 w-4" />
              <span className="min-w-0 flex-1">
                Click a slot, then drag it to move, its handles to resize, or the centre knob to rotate.
                <span className="text-muted-foreground"> {editCount} change{editCount === 1 ? "" : "s"} not saved.</span>
              </span>
              <Button size="sm" variant="outline" onClick={duplicateSelectedSlot} disabled={!selectedEditable}>
                <Copy className="mr-1.5 h-3.5 w-3.5" /> Duplicate
              </Button>
              <Button size="sm" variant="outline" onClick={deleteSelectedSlot} disabled={!selectedEditable}>
                <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete
              </Button>
              <Button size="sm" variant="ghost" onClick={leaveEditMode} disabled={savingEdits}>Cancel</Button>
              <Button size="sm" onClick={saveEdits} disabled={editCount === 0 || savingEdits} data-testid="parking-save-edits">
                {savingEdits ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Check className="mr-1.5 h-3.5 w-3.5" />}
                Save changes
              </Button>
            </div>
          )}
          {areaTool && (
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-400/50 bg-amber-400/10 px-3 py-2 text-sm" data-testid="parking-area-bar">
              <Move className="h-4 w-4" />
              <span className="min-w-0 flex-1">
                <span className="font-medium">{toolArea?.name ?? "Area"}</span>: drag the area to move it, drag the centre knob to rotate it.
                <span className="text-muted-foreground">
                  {" "}{areaMove.east.toFixed(1)} m E, {areaMove.north.toFixed(1)} m N, {areaMove.deg.toFixed(0)}°
                </span>
              </span>
              <Button size="sm" variant="ghost" onClick={cancelAreaTool} disabled={savingArea}>Cancel</Button>
              <Button
                size="sm" onClick={saveAreaTool} data-testid="parking-save-area-move"
                disabled={savingArea || (!areaMove.east && !areaMove.north && !areaMove.deg)}
              >
                {savingArea ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Check className="mr-1.5 h-3.5 w-3.5" />}
                Save
              </Button>
            </div>
          )}

          <div className="relative h-[520px]">
            {board ? (
              <ParkingMap
                center={center}
                slots={mapSlots}
                outlines={outlines}
                interaction={editMode ? "slots" : areaTool ? "area" : "view"}
                selectedSlotId={selectedSlotId}
                areaTool={toolArea ? { areaId: toolArea.id, center: toolCenter } : null}
                onSlotClick={(id) => { if (!areaTool) setSelectedSlotId(id); }}
                onSlotContextMenu={onSlotContextMenu}
                onSlotChange={onSlotChange}
                onAreaMove={onAreaMove}
                onAreaRotate={onAreaRotate}
                fitKey={board.id}
                testId="parking-board-map"
              />
            ) : (
              <SkeletonMap className="h-full" />
            )}

            {menu && isManager && (editMode || menuAreaObj) && (
              <div
                className="absolute z-20 min-w-[190px] overflow-hidden rounded-lg border border-border bg-popover py-1 text-popover-foreground shadow-lg"
                style={{ left: menu.x, top: menu.y }}
                onClick={(e) => e.stopPropagation()}
                data-testid="parking-context-menu"
              >
                {editMode ? (
                  <>
                    <button className={menuItem} onClick={() => { setMenu(null); duplicateSelectedSlot(); }}>
                      <Copy className="h-4 w-4" /> Duplicate slot
                    </button>
                    <button className={menuItem} onClick={() => { setMenu(null); deleteSelectedSlot(); }}>
                      <Trash2 className="h-4 w-4" /> Delete slot
                    </button>
                  </>
                ) : (
                  <>
                    <div className="truncate px-3 py-1 text-xs font-medium text-muted-foreground">{menuAreaObj.name}</div>
                    <button className={menuItem} onClick={() => { setMenu(null); setDialog({ edit: menuAreaObj }); }}>
                      <Pencil className="h-4 w-4" /> Edit area…
                    </button>
                    <button className={menuItem} onClick={() => { setMenu(null); startAreaTool(menuAreaObj.id); }}>
                      <Move className="h-4 w-4" /> Move / rotate
                    </button>
                    <button className={menuItem} onClick={() => { setMenu(null); duplicateArea(menuAreaObj.id); }}>
                      <Copy className="h-4 w-4" /> Duplicate area
                    </button>
                    <button className={menuItem} onClick={() => { setMenu(null); setConfirm({ area: menuAreaObj }); }}>
                      <Trash2 className="h-4 w-4" /> Delete area
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
          {isManager && !busy && (
            <p className="text-xs text-muted-foreground">
              Right-click an area on the map to edit, move, rotate, duplicate or delete it.
              Turn on “Edit slots” to work on single slots instead.
            </p>
          )}
        </div>

        <div className="space-y-4">
          {selectedSlot && (
            <Card className="border-border/70 bg-card/60 p-4" data-testid="parking-slot-detail">
              <div className="flex items-center gap-2">
                <span className={cn("h-2.5 w-2.5 rounded-full", selectedSlot.occupied ? "bg-red-500" : "bg-emerald-500")} />
                <span className="text-sm font-medium">Slot #{selectedSlot.number}</span>
                <span className="text-xs text-muted-foreground">{areaName(selectedSlot.areaId)}</span>
                <button className="ml-auto text-muted-foreground hover:text-foreground" onClick={() => setSelectedSlotId(null)} aria-label="Close">
                  <X className="h-4 w-4" />
                </button>
              </div>
              {selectedSlot.occupied && selectedSlot.vehicle ? (
                <div className="mt-3 space-y-2">
                  <button
                    onClick={() => navigate(`/vehicles/${selectedSlot.vehicle.id}`)}
                    className="flex w-full items-center gap-3 rounded-xl border border-border/50 bg-secondary/20 p-2.5 text-left transition hover:border-border hover:bg-secondary/40"
                  >
                    <Car className="h-4 w-4 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{selectedSlot.vehicle.name}</div>
                      <div className="truncate text-xs text-muted-foreground">
                        {[selectedSlot.vehicle.plate, selectedSlot.vehicle.vin].filter(Boolean).join(" · ") || "—"}
                      </div>
                    </div>
                  </button>
                  <p className="text-xs text-muted-foreground">
                    Parked {selectedSlot.occupiedSince ? relativeTime(selectedSlot.occupiedSince) : "—"}
                  </p>
                  {isOperator && (
                    <Button size="sm" variant="outline" onClick={() => setConfirm("release")} data-testid="parking-release-slot">
                      Mark vacant
                    </Button>
                  )}
                </div>
              ) : (
                <p className="mt-3 text-sm text-muted-foreground">Vacant.</p>
              )}
            </Card>
          )}

          <Card className="border-border/70 bg-card/60 p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-medium">
              <span className="h-2 w-2 rounded-full bg-red-500" /> Parked
              <span className="ml-auto text-muted-foreground">{occupiedSlots.length}</span>
            </div>
            <div className="max-h-[240px] space-y-2 overflow-y-auto pr-1">
              {boardLoading && !board ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
              ) : occupiedSlots.length === 0 ? (
                <p className="text-sm text-muted-foreground">No vehicles are parked in this lot.</p>
              ) : (
                occupiedSlots.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setSelectedSlotId(s.id)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-xl border bg-secondary/20 p-2.5 text-left transition hover:bg-secondary/40",
                      s.id === selectedSlotId ? "border-primary/60" : "border-border/50"
                    )}
                  >
                    <span className="flex h-7 min-w-7 items-center justify-center rounded-md bg-secondary/60 px-1.5 font-telemetry text-xs">
                      {s.number}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm">{s.vehicle?.name || "Vehicle"}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {s.occupiedSince ? relativeTime(s.occupiedSince) : ""}
                    </span>
                  </button>
                ))
              )}
            </div>
          </Card>

          {board && (
            <Card className="border-border/70 bg-card/60 p-4" data-testid="parking-areas">
              <div className="mb-3 flex items-center gap-2 text-sm font-medium">
                <span className="h-2 w-2 rounded-full bg-amber-400" /> Areas
                <span className="ml-auto text-muted-foreground">{board.areas?.length ?? 0}</span>
              </div>
              <div className="space-y-2">
                {(board.areas || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">This lot has no areas yet. Add one to create slots.</p>
                ) : (
                  board.areas.map((a) => (
                    <div
                      key={a.id}
                      className={cn(
                        "flex items-center gap-2 rounded-xl border bg-secondary/20 p-2.5",
                        areaTool?.areaId === a.id ? "border-amber-400/60" : "border-border/50"
                      )}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium">{a.name}</div>
                        <div className="truncate text-xs text-muted-foreground">
                          {a.slotCount} slots · {a.cols}/row · {a.rowsPerBlock} row{a.rowsPerBlock === 1 ? "" : "s"}/block
                        </div>
                      </div>
                      {isManager && !busy && (
                        <div className="flex shrink-0 items-center gap-1 text-muted-foreground">
                          <button className="rounded p-1 transition hover:bg-secondary/60 hover:text-foreground"
                            onClick={() => setDialog({ edit: a })} aria-label={`Edit ${a.name}`} title="Edit area">
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button className="rounded p-1 transition hover:bg-secondary/60 hover:text-foreground"
                            onClick={() => startAreaTool(a.id)} aria-label={`Move or rotate ${a.name}`} title="Move / rotate">
                            <Move className="h-4 w-4" />
                          </button>
                          <button className="rounded p-1 transition hover:bg-secondary/60 hover:text-foreground"
                            onClick={() => duplicateArea(a.id)} aria-label={`Duplicate ${a.name}`} title="Duplicate area">
                            <Copy className="h-4 w-4" />
                          </button>
                          <button className="rounded p-1 transition hover:bg-secondary/60 hover:text-foreground"
                            onClick={() => setConfirm({ area: a })} aria-label={`Delete ${a.name}`} title="Delete area">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </Card>
          )}

          {isManager && board && (
            <Card className="border-border/70 bg-card/60 p-4">
              <label className="flex items-center justify-between gap-3 text-sm">
                <span>
                  Edit slots
                  <span className="block text-xs text-muted-foreground">
                    {editMode
                      ? "Working on single slots: move, resize, rotate, duplicate."
                      : "Off: work on whole areas (right-click the map)."}
                  </span>
                </span>
                <Switch
                  checked={editMode}
                  disabled={!!areaTool || total === 0}
                  onCheckedChange={(v) => (v ? (setEditMode(true), setSelectedSlotId(null)) : leaveEditMode())}
                  data-testid="parking-edit-slots"
                />
              </label>
              <label className="mt-4 flex items-center justify-between gap-3 text-sm">
                <span>
                  Track this lot
                  <span className="block text-xs text-muted-foreground">Pausing stops parking updates and alerts.</span>
                </span>
                <Switch checked={board.isActive} onCheckedChange={toggleActive} />
              </label>
              <Button size="sm" variant="ghost" className="mt-3 text-muted-foreground" onClick={() => setConfirm("layout")} disabled={busy}>
                <Trash2 className="mr-1.5 h-4 w-4" /> Delete lot
              </Button>
            </Card>
          )}
        </div>
      </div>

      {dialogs}
      <ConfirmDialog
        open={confirm === "layout"}
        onOpenChange={(o) => !o && setConfirm(null)}
        title="Delete this parking lot?"
        description="All of its areas, slots and their occupancy are removed. Vehicles are not affected."
        confirmLabel="Delete lot"
        destructive
        onConfirm={removeLayout}
      />
      <ConfirmDialog
        open={confirm === "release"}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={`Mark slot #${selectedSlot?.number ?? ""} vacant?`}
        description="Use this when a vehicle was placed in the wrong slot. If the vehicle is still physically there, it is picked up again on the next position check."
        confirmLabel="Mark vacant"
        onConfirm={releaseSlot}
      />
      <ConfirmDialog
        open={!!confirm?.area}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={`Delete ${confirm?.area?.name ?? "this area"}?`}
        description="Its slots are removed with it; the rest of the lot is untouched. Vehicles parked in those slots are freed."
        confirmLabel="Delete area"
        destructive
        onConfirm={() => removeArea(confirm.area)}
      />
    </section>
  );
}
